import {css, cx} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {FormProviderRHF} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {KM_PREFERENCES_FORM_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {Modal} from "@/components/modal/Modal";
import {zIndexes} from "@/features/km_app/constants/z_indexes";
import {prettyScroll, prettyScrollProps} from "@/styles/receipes/pretty_scroll";

import {HOME_MATCH_PREFERENCES_SURVEY_CONTENT} from "../../constants/preferences/home_match_preferences_survey_content";
import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../../constants/preferences/home_match_preferences_wizard_content";
import {useHomeMatchPreferencesSurveyModal} from "../../hooks/preferences/use_home_match_preferences_survey_modal";
import {HomeMatchPreferencesWizardExitLayer, HomeMatchPreferencesWizardExitSheet} from "../home_match_preferences_wizard/HomeMatchPreferencesWizardExit";
import {HomeMatchPreferencesWizardHost} from "../home_match_preferences_wizard/HomeMatchPreferencesWizardHost";
import {HomeMatchPreferencesWizardSummaryStep} from "../home_match_preferences_wizard/HomeMatchPreferencesWizardSummaryStep";
import {
    HomeMatchPreferencesSurveyActions,
    HomeMatchPreferencesSurveyConfirmAction,
    HomeMatchPreferencesSurveyLocationActions
} from "./HomeMatchPreferencesSurveyActions";
import {HomeMatchPreferencesSurveyFields} from "./HomeMatchPreferencesSurveyFields";

const {CLOSE, DESCRIPTION, EXIT_DESCRIPTION, EXIT_LEAVE, EXIT_RETURN, EXIT_RETURN_SUMMARY, EXIT_TITLE, TITLE} = HOME_MATCH_PREFERENCES_SURVEY_CONTENT;
const {DESCRIPTION: SUMMARY_DESCRIPTION, TITLE: SUMMARY_TITLE} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT.SUMMARY;

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    preferencesSurveySource?: PreferencesSurveySource;
    showSentLeadMessage?: boolean;
}

export const HomeMatchPreferencesSurveyModal = (props: IProps) => {
    const {isOpen, onClose, preferencesSurveySource = PreferencesSurveySource.PREFERENCES, showSentLeadMessage = false} = props;
    const {isMobile} = useUserDevice();
    const {
        canSave,
        closeExit,
        closeParameterStep,
        confirmSummary,
        discardSurvey,
        dismissExit,
        editSummaryStep,
        handleAfterClose,
        handleSubmit,
        homeMatchBasicConsent,
        isExitOpen,
        isLoading,
        isLocationMapOpen,
        isSaving,
        isSummaryOpen,
        isSwitchingHomeMatchPreferences,
        locationMapActions,
        methods,
        onSubmit,
        openSurveyFromSummary,
        parameterStep,
        parameterStepLocation,
        requestClose,
        setHomeMatchBasicConsent,
        setIsLocationMapOpen,
        setLocationMapActions,
        summary,
        summaryContentRef
    } = useHomeMatchPreferencesSurveyModal({isOpen, onClose, preferencesSurveySource});
    const exitProps = {
        description: EXIT_DESCRIPTION,
        onDismiss: dismissExit,
        onReturn: closeExit,
        onSave: dismissExit,
        returnLabel: isSummaryOpen ? EXIT_RETURN_SUMMARY : EXIT_RETURN,
        saveLabel: EXIT_LEAVE,
        title: EXIT_TITLE
    };

    return (
        <>
            <Modal
                className={modalStyle}
                dataTestId={KM_PREFERENCES_FORM_TEST_IDS.MODAL}
                isOpen={isOpen}
                onAfterClose={handleAfterClose}
                onModalClose={requestClose}
                shouldCloseOnOverlayClick={false}
                showCloseButton={false}
                zIndex={zIndexes.topModal}
            >
                <div className={shellStyle} data-testid={KM_PREFERENCES_FORM_TEST_IDS.MODAL}>
                    <Modal.TopBar closeLabel={CLOSE} closeTestId={KM_PREFERENCES_FORM_TEST_IDS.CLOSE} onClose={requestClose} />

                    {isLoading ? (
                        <div className={contentStyle} {...prettyScrollProps}>
                            <div className={loaderStyle}>
                                <Loader size="md" />
                            </div>
                        </div>
                    ) : (
                        <FormProviderRHF {...methods}>
                            <form className={formStyle} onSubmit={handleSubmit(onSubmit)}>
                                <div className={contentStyle} ref={summaryContentRef} {...prettyScrollProps}>
                                    <div className={titleCopyStyle}>
                                        <Text as="h2" color="var(--colors-secondary)" preserveFontSize variant="headline_5">
                                            {isSummaryOpen ? SUMMARY_TITLE : TITLE}
                                        </Text>
                                        <Text color="var(--colors-gray-800)" preserveFontSize variant="body_copy_2">
                                            {isSummaryOpen ? SUMMARY_DESCRIPTION : DESCRIPTION}
                                        </Text>
                                    </div>

                                    {isSummaryOpen && (
                                        <HomeMatchPreferencesWizardSummaryStep
                                            consent={homeMatchBasicConsent}
                                            onChangeProperty={openSurveyFromSummary}
                                            onChangeStep={editSummaryStep}
                                            onConsentChange={setHomeMatchBasicConsent}
                                            summary={summary}
                                        />
                                    )}

                                    <div className={isSummaryOpen ? surveyFieldsHiddenStyle : surveyFieldsStyle}>
                                        <HomeMatchPreferencesSurveyFields
                                            isLocationMapOpen={isLocationMapOpen}
                                            isMobile={isMobile}
                                            isOpen={isOpen}
                                            locationMapActions={locationMapActions}
                                            onMapActionsChange={setLocationMapActions}
                                            onMapOpenChange={setIsLocationMapOpen}
                                            showSentLeadMessage={showSentLeadMessage}
                                        />
                                    </div>

                                    {!isMobile && (
                                        <div className={cx(ctaFooterStyle, !isSummaryOpen && ctaFooterDividerStyle)}>
                                            <div className={documentActionsStyle}>
                                                {isSummaryOpen ? (
                                                    <HomeMatchPreferencesSurveyConfirmAction
                                                        compact
                                                        isSaving={isSwitchingHomeMatchPreferences}
                                                        onConfirm={confirmSummary}
                                                    />
                                                ) : (
                                                    <HomeMatchPreferencesSurveyActions
                                                        compactSave
                                                        isSaveDisabled={!canSave}
                                                        isSaving={isSaving}
                                                        onDiscard={discardSurvey}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {isMobile && isSummaryOpen && (
                                    <Modal.FloatingActions>
                                        <HomeMatchPreferencesSurveyConfirmAction isSaving={isSwitchingHomeMatchPreferences} onConfirm={confirmSummary} />
                                    </Modal.FloatingActions>
                                )}

                                {isMobile && !isSummaryOpen && !isLocationMapOpen && (
                                    <Modal.FloatingActions className={mobileSurveyFloatingStyle} stacked>
                                        <HomeMatchPreferencesSurveyActions isSaveDisabled={!canSave} isSaving={isSaving} onDiscard={discardSurvey} />
                                    </Modal.FloatingActions>
                                )}

                                {isMobile && !isSummaryOpen && isLocationMapOpen && locationMapActions && (
                                    <Modal.FloatingActions className={mobileSurveyFloatingStyle} stacked>
                                        <HomeMatchPreferencesSurveyLocationActions onApply={locationMapActions.apply} onCancel={locationMapActions.cancel} />
                                    </Modal.FloatingActions>
                                )}
                            </form>
                        </FormProviderRHF>
                    )}
                </div>

                {isExitOpen && !isMobile && (
                    <div className={exitCoverStyle}>
                        <HomeMatchPreferencesWizardExitLayer {...exitProps} />
                    </div>
                )}
            </Modal>

            {isMobile && <HomeMatchPreferencesWizardExitSheet {...exitProps} isOpen={isOpen && isExitOpen} zIndex={zIndexes.landingOverlay} />}

            {parameterStep && (
                <HomeMatchPreferencesWizardHost
                    amplitudeLocation={parameterStepLocation}
                    isOpen
                    onClose={closeParameterStep}
                    onSurveyStepsDone={closeParameterStep}
                    preferencesSurveySource={preferencesSurveySource}
                    request={{entry: "parameter", returnsToSummary: true, startStep: parameterStep}}
                    zIndex={zIndexes.landingOverlay}
                />
            )}
        </>
    );
};

const modalStyle = css({
    borderRadius: 0,
    height: "100%",
    inset: 0,
    maxHeight: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    width: "100%",

    md: {
        bottom: "auto",
        height: "auto",
        left: 0,
        marginInline: "auto",
        maxHeight: "calc(100vh - 9.6rem)",
        right: 0,
        top: "4.8rem",
        width: "80rem"
    }
});

const shellStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    position: "relative",

    md: {
        maxHeight: "90vh"
    }
});

const contentStyle = css(prettyScroll, {
    display: "flex",
    flex: "1",
    flexDirection: "column",
    minHeight: 0,
    p: "1.6rem 1.2rem 4.8rem",

    "& > *": {
        flexShrink: 0
    },

    md: {
        p: "2.4rem 6rem 0"
    }
});

const surveyFieldsStyle = css({
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,

    "& > *": {
        flexShrink: 0
    }
});

const surveyFieldsHiddenStyle = css({
    display: "none"
});

const formStyle = css({
    display: "flex",
    flex: "1",
    flexDirection: "column",
    minHeight: 0
});

const ctaFooterStyle = css({
    flexShrink: 0,
    pt: 8
});

const ctaFooterDividerStyle = css({
    borderTop: "1px solid var(--colors-gray-300)"
});

const documentActionsStyle = css({
    alignItems: "center",
    display: "flex",
    flex: "0 0 9.6rem",
    height: "9.6rem",
    justifyContent: "space-between",
    minHeight: "9.6rem"
});

const mobileSurveyFloatingStyle = css({
    "&&[data-stacked]": {
        flexDirection: "column-reverse"
    }
});

const exitCoverStyle = css({
    alignItems: "center",
    backgroundColor: "rgb(from var(--colors-secondary) r g b / 0.35)",
    display: "flex",
    inset: 0,
    justifyContent: "center",
    position: "absolute",
    zIndex: "var(--zIndex-raised)"
});

const titleCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mb: 5
});

const loaderStyle = css({
    display: "flex",
    justifyContent: "center",
    py: 4
});
import {css, cx} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {FormProviderRHF} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {KM_PREFERENCES_FORM_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {Modal} from "@/components/modal/Modal";
import {zIndexes} from "@/features/km_app/constants/z_indexes";
import {prettyScroll, prettyScrollProps} from "@/styles/receipes/pretty_scroll";

import {HOME_MATCH_PREFERENCES_SURVEY_CONTENT} from "../../constants/preferences/home_match_preferences_survey_content";
import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../../constants/preferences/home_match_preferences_wizard_content";
import {useHomeMatchPreferencesSurveyModal} from "../../hooks/preferences/use_home_match_preferences_survey_modal";
import {HomeMatchPreferencesWizardExitLayer, HomeMatchPreferencesWizardExitSheet} from "../home_match_preferences_wizard/HomeMatchPreferencesWizardExit";
import {HomeMatchPreferencesWizardHost} from "../home_match_preferences_wizard/HomeMatchPreferencesWizardHost";
import {HomeMatchPreferencesWizardSummaryStep} from "../home_match_preferences_wizard/HomeMatchPreferencesWizardSummaryStep";
import {
    HomeMatchPreferencesSurveyActions,
    HomeMatchPreferencesSurveyConfirmAction,
    HomeMatchPreferencesSurveyLocationActions
} from "./HomeMatchPreferencesSurveyActions";
import {HomeMatchPreferencesSurveyFields} from "./HomeMatchPreferencesSurveyFields";

const {CLOSE, DESCRIPTION, EXIT_DESCRIPTION, EXIT_LEAVE, EXIT_RETURN, EXIT_RETURN_SUMMARY, EXIT_TITLE, TITLE} = HOME_MATCH_PREFERENCES_SURVEY_CONTENT;
const {DESCRIPTION: SUMMARY_DESCRIPTION, TITLE: SUMMARY_TITLE} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT.SUMMARY;

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    preferencesSurveySource?: PreferencesSurveySource;
    showSentLeadMessage?: boolean;
}

export const HomeMatchPreferencesSurveyModal = (props: IProps) => {
    const {isOpen, onClose, preferencesSurveySource = PreferencesSurveySource.PREFERENCES, showSentLeadMessage = false} = props;
    const {isMobile} = useUserDevice();
    const {
        canSave,
        closeExit,
        closeParameterStep,
        confirmSummary,
        discardSurvey,
        dismissExit,
        editSummaryStep,
        handleAfterClose,
        handleSubmit,
        homeMatchBasicConsent,
        isExitOpen,
        isLoading,
        isLocationMapOpen,
        isSaving,
        isSummaryOpen,
        isSwitchingHomeMatchPreferences,
        locationMapActions,
        methods,
        onSubmit,
        openSurveyFromSummary,
        parameterStep,
        parameterStepLocation,
        requestClose,
        setHomeMatchBasicConsent,
        setIsLocationMapOpen,
        setLocationMapActions,
        summary,
        summaryContentRef
    } = useHomeMatchPreferencesSurveyModal({isOpen, onClose, preferencesSurveySource});
    const exitProps = {
        description: EXIT_DESCRIPTION,
        onDismiss: dismissExit,
        onReturn: closeExit,
        onSave: dismissExit,
        returnLabel: isSummaryOpen ? EXIT_RETURN_SUMMARY : EXIT_RETURN,
        saveLabel: EXIT_LEAVE,
        title: EXIT_TITLE
    };

    return (
        <>
            <Modal
                className={modalStyle}
                dataTestId={KM_PREFERENCES_FORM_TEST_IDS.MODAL}
                isOpen={isOpen}
                onAfterClose={handleAfterClose}
                onModalClose={requestClose}
                shouldCloseOnOverlayClick={false}
                showCloseButton={false}
                zIndex={zIndexes.topModal}
            >
                <div className={shellStyle} data-testid={KM_PREFERENCES_FORM_TEST_IDS.MODAL}>
                    <Modal.TopBar closeLabel={CLOSE} closeTestId={KM_PREFERENCES_FORM_TEST_IDS.CLOSE} onClose={requestClose} />

                    {isLoading ? (
                        <div className={contentStyle} {...prettyScrollProps}>
                            <div className={loaderStyle}>
                                <Loader size="md" />
                            </div>
                        </div>
                    ) : (
                        <FormProviderRHF {...methods}>
                            <form className={formStyle} onSubmit={handleSubmit(onSubmit)}>
                                <div className={contentStyle} ref={summaryContentRef} {...prettyScrollProps}>
                                    <div className={titleCopyStyle}>
                                        <Text as="h2" color="var(--colors-secondary)" preserveFontSize variant="headline_5">
                                            {isSummaryOpen ? SUMMARY_TITLE : TITLE}
                                        </Text>
                                        <Text color="var(--colors-gray-800)" preserveFontSize variant="body_copy_2">
                                            {isSummaryOpen ? SUMMARY_DESCRIPTION : DESCRIPTION}
                                        </Text>
                                    </div>

                                    {isSummaryOpen && (
                                        <HomeMatchPreferencesWizardSummaryStep
                                            consent={homeMatchBasicConsent}
                                            onChangeProperty={openSurveyFromSummary}
                                            onChangeStep={editSummaryStep}
                                            onConsentChange={setHomeMatchBasicConsent}
                                            summary={summary}
                                        />
                                    )}

                                    <div className={isSummaryOpen ? surveyFieldsHiddenStyle : surveyFieldsStyle}>
                                        <HomeMatchPreferencesSurveyFields
                                            isLocationMapOpen={isLocationMapOpen}
                                            isMobile={isMobile}
                                            isOpen={isOpen}
                                            locationMapActions={locationMapActions}
                                            onMapActionsChange={setLocationMapActions}
                                            onMapOpenChange={setIsLocationMapOpen}
                                            showSentLeadMessage={showSentLeadMessage}
                                        />
                                    </div>

                                    {!isMobile && (
                                        <div className={cx(ctaFooterStyle, !isSummaryOpen && ctaFooterDividerStyle)}>
                                            <div className={documentActionsStyle}>
                                                {isSummaryOpen ? (
                                                    <HomeMatchPreferencesSurveyConfirmAction
                                                        compact
                                                        isSaving={isSwitchingHomeMatchPreferences}
                                                        onConfirm={confirmSummary}
                                                    />
                                                ) : (
                                                    <HomeMatchPreferencesSurveyActions
                                                        compactSave
                                                        isSaveDisabled={!canSave}
                                                        isSaving={isSaving}
                                                        onDiscard={discardSurvey}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {isMobile && isSummaryOpen && (
                                    <Modal.FloatingActions>
                                        <HomeMatchPreferencesSurveyConfirmAction isSaving={isSwitchingHomeMatchPreferences} onConfirm={confirmSummary} />
                                    </Modal.FloatingActions>
                                )}

                                {isMobile && !isSummaryOpen && !isLocationMapOpen && (
                                    <Modal.FloatingActions className={mobileSurveyFloatingStyle} stacked>
                                        <HomeMatchPreferencesSurveyActions isSaveDisabled={!canSave} isSaving={isSaving} onDiscard={discardSurvey} />
                                    </Modal.FloatingActions>
                                )}

                                {isMobile && !isSummaryOpen && isLocationMapOpen && locationMapActions && (
                                    <Modal.FloatingActions className={mobileSurveyFloatingStyle} stacked>
                                        <HomeMatchPreferencesSurveyLocationActions onApply={locationMapActions.apply} onCancel={locationMapActions.cancel} />
                                    </Modal.FloatingActions>
                                )}
                            </form>
                        </FormProviderRHF>
                    )}
                </div>

                {isExitOpen && !isMobile && (
                    <div className={exitCoverStyle}>
                        <HomeMatchPreferencesWizardExitLayer {...exitProps} />
                    </div>
                )}
            </Modal>

            {isMobile && <HomeMatchPreferencesWizardExitSheet {...exitProps} isOpen={isOpen && isExitOpen} zIndex={zIndexes.landingOverlay} />}

            {parameterStep && (
                <HomeMatchPreferencesWizardHost
                    amplitudeLocation={parameterStepLocation}
                    isOpen
                    onClose={closeParameterStep}
                    onSurveyStepsDone={closeParameterStep}
                    preferencesSurveySource={preferencesSurveySource}
                    request={{entry: "parameter", returnsToSummary: true, startStep: parameterStep}}
                    zIndex={zIndexes.landingOverlay}
                />
            )}
        </>
    );
};

const modalStyle = css({
    borderRadius: 0,
    height: "100%",
    inset: 0,
    maxHeight: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    width: "100%",

    md: {
        bottom: "auto",
        height: "auto",
        left: 0,
        marginInline: "auto",
        maxHeight: "calc(100vh - 9.6rem)",
        right: 0,
        top: "4.8rem",
        width: "80rem"
    }
});

const shellStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    position: "relative",

    md: {
        maxHeight: "90vh"
    }
});

const contentStyle = css(prettyScroll, {
    display: "flex",
    flex: "1",
    flexDirection: "column",
    minHeight: 0,
    p: "1.6rem 1.2rem 4.8rem",

    "& > *": {
        flexShrink: 0
    },

    md: {
        p: "2.4rem 6rem 0"
    }
});

const surveyFieldsStyle = css({
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,

    "& > *": {
        flexShrink: 0
    }
});

const surveyFieldsHiddenStyle = css({
    display: "none"
});

const formStyle = css({
    display: "flex",
    flex: "1",
    flexDirection: "column",
    minHeight: 0
});

const ctaFooterStyle = css({
    flexShrink: 0,
    pt: 8
});

const ctaFooterDividerStyle = css({
    borderTop: "1px solid var(--colors-gray-300)"
});

const documentActionsStyle = css({
    alignItems: "center",
    display: "flex",
    flex: "0 0 9.6rem",
    height: "9.6rem",
    justifyContent: "space-between",
    minHeight: "9.6rem"
});

const mobileSurveyFloatingStyle = css({
    "&&[data-stacked]": {
        flexDirection: "column-reverse"
    }
});

const exitCoverStyle = css({
    alignItems: "center",
    backgroundColor: "rgb(from var(--colors-secondary) r g b / 0.35)",
    display: "flex",
    inset: 0,
    justifyContent: "center",
    position: "absolute",
    zIndex: "var(--zIndex-raised)"
});

const titleCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mb: 5
});

const loaderStyle = css({
    display: "flex",
    justifyContent: "center",
    py: 4
});
